import React, { useState, useRef, useEffect } from 'react';
import LoaderSvg from './Loader.svg';
import './ImageWithLoader.css';

interface ImageWithLoaderProps {
  src: string;
  alt: string;
  className?: string;
  loaderSize?: number;
  onLoad?: () => void;
  onError?: () => void;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  src,
  alt,
  className = '',
  loaderSize = 40,
  onLoad,
  onError
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setShowLoader(false);

    // Check if image is already loaded
    if (imgRef.current?.complete) {
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowLoader(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  return (
    <div className={`image-with-loader-container ${className}`} style={{ position: 'relative', overflow: 'hidden' }}>
      {isLoading && showLoader && (
        <div 
          className="loader-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000000',
            zIndex: 2
          }}
        >
          <img 
            src={LoaderSvg} 
            alt="Loading..." 
            className="loader-spinner"
            style={{
              width: loaderSize,
              height: loaderSize,
              animation: 'spin 1s linear infinite'
            }}
          />
        </div>
      )}
      
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={className}
        style={{
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          objectFit: 'cover'
        }}
      />
      
      {hasError && (
        <div 
          className="error-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000000',
            color: '#F7F3E9',
            fontSize: '14px',
            zIndex: 2
          }}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ opacity: 0.5 }}
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
            <line x1="3" y1="3" x2="21" y2="21"></line>
          </svg>
        </div>
      )}
    </div>
  );
};

export default ImageWithLoader;
